import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetRouletteRoundsQuery } from '../services/roulette/rouletteApi';

const THEME = '#7c3aed';

const RouletteRoundsPage = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, error } = useGetRouletteRoundsQuery(
    { fromDate: date, toDate: date, page, limit: 20 },
    { skip: !date }
  );

  const rounds     = data?.data || [];
  const summary    = data?.summary || {};
  const totalPages = data?.pagination?.totalPages || 1;

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-6xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            <div className="px-5 py-3 flex items-center justify-between" style={{ background: THEME }}>
              <span className="text-xl text-white font-normal">Roulette Rounds — {date}</span>
              <button onClick={() => navigate(-1)} className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors">
                Back
              </button>
            </div>

            {summary.overallPL !== undefined && (
              <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 text-sm">
                <span className="text-gray-500 mr-2">Total P/L:</span>
                <span className={`font-semibold ${summary.overallPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Number(summary.overallPL).toFixed(2)}
                </span>
              </div>
            )}

            <div className="overflow-x-auto">
              {isLoading || isFetching ? (
                <div className="flex justify-center py-8"><Loading /></div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="text-white" style={{ background: THEME }}>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">#</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">Round ID</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">Date & Time</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">Result</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">Total Bets</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">Total Stake</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-400">P/L</th>
                      <th className="py-3 px-4 text-left font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {rounds.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-gray-400">No rounds found</td>
                      </tr>
                    ) : (
                      rounds.map((round, index) => (
                        <tr key={round.roundId || index} className="hover:bg-gray-50 border-t border-gray-200">
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-500">{(page - 1) * 20 + index + 1}</td>
                          <td className="py-3 px-4 border-r border-gray-200 text-xs font-mono text-gray-600">{round.roundId || '-'}</td>
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-700">
                            {round.settledAt || round.createdAt
                              ? new Date(round.settledAt || round.createdAt).toLocaleString('en-GB', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
                                })
                              : '-'}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            {round.result != null ? (
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-bold
                                ${round.result === 0 ? 'bg-green-600' : round.resultColor === 'red' ? 'bg-red-600' : 'bg-gray-800'}`}>
                                {round.result}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-700">{round.totalBets ?? '-'}</td>
                          <td className="py-3 px-4 border-r border-gray-200 text-gray-700">{round.totalStake ?? '-'}</td>
                          <td className={`py-3 px-4 border-r border-gray-200 font-semibold ${(round.pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {Number(round.pl || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => navigate(`/roulette/round-bets/${round.roundId}`)}
                              className="text-white px-4 py-1.5 text-sm transition-colors hover:opacity-90"
                              style={{ background: THEME }}
                            >
                              Show Bets
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-end gap-1 px-4 py-3 border-t border-gray-200">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">‹</button>
                <span className="px-3 py-1 text-white text-sm" style={{ background: THEME }}>{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouletteRoundsPage;
